import React, { useMemo, useState } from 'react';
import styles from '../styles/AdminPage.module.css';
import usePlatformHealth from '../hooks/usePlatformHealth';
import {
  GROUP_OPTIONS,
  KIND_OPTIONS,
  WINDOW_OPTIONS,
  errorTrendSpec,
  formatCount,
  formatDuration,
  formatRate,
  groupLabel,
  labelOf,
  latencyTrendSpec,
} from '../utils/platformHealth';
import VegaChart from './VegaChart';

const Picker = ({ label, value, options, onChange }) => (
  <label className={styles.healthPicker}>
    {label}
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </label>
);

const Tile = ({ label, value }) => (
  <div className={styles.statCard}>
    <div className={styles.statValue}>{value}</div>
    <div className={styles.statLabel}>{label}</div>
  </div>
);

const Tiles = ({ overall, kind }) => (
  <div className={styles.healthTiles}>
    <Tile label={labelOf(KIND_OPTIONS, kind)} value={formatCount(overall.count)} />
    <Tile label="P50" value={formatDuration(overall.p50_ms)} />
    <Tile label="P95" value={formatDuration(overall.p95_ms)} />
    <Tile label="P99" value={formatDuration(overall.p99_ms)} />
    <Tile label="Error rate" value={formatRate(overall.error_rate)} />
    <Tile label="Timeout rate" value={formatRate(overall.timeout_rate)} />
  </div>
);

const Notes = ({ summary }) => (
  <>
    {summary.approximate && (
      <p className={styles.healthNote}>
        Percentiles are estimates, within about 10%. This window starts at
        midnight UTC on {summary.since.slice(0, 10)}.
      </p>
    )}
    {summary.truncated && (
      <p className={styles.healthNote}>
        This window has more rows than one read allows, so its oldest part is
        left out.
      </p>
    )}
    {summary.group_count > summary.groups.length && (
      <p className={styles.healthNote}>
        Showing the {summary.groups.length} busiest of {summary.group_count}.
      </p>
    )}
  </>
);

const RollupsBehind = ({ through }) => (
  <p className={styles.healthAlert}>
    Daily rollups are behind{through ? `: the newest is ${through}` : ''}. This
    is normal for a few minutes after a deploy. If it stays, check the
    orchestrator's logs, since raw rows aren't pruned until it clears.
  </p>
);

const Trend = ({ days }) => {
  const latency = useMemo(() => latencyTrendSpec(days), [days]);
  const errors = useMemo(() => errorTrendSpec(days), [days]);
  return (
    <div className={styles.healthTrend}>
      <VegaChart spec={latency} />
      <VegaChart spec={errors} />
    </div>
  );
};

const GroupTable = ({ groups, groupBy }) => (
  <div className={styles.userTable}>
    <table className={styles.healthTable}>
      <thead>
        <tr>
          <th>{labelOf(GROUP_OPTIONS, groupBy)}</th>
          <th>Events</th>
          <th>P50</th>
          <th>P95</th>
          <th>P99</th>
          <th>Error rate</th>
          <th>Timeout rate</th>
          <th>Rejected</th>
          <th>Cancelled</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => (
          <tr key={groupLabel(group, groupBy)}>
            <td>{groupLabel(group, groupBy)}</td>
            <td>{formatCount(group.count)}</td>
            <td>{formatDuration(group.p50_ms)}</td>
            <td>{formatDuration(group.p95_ms)}</td>
            <td>{formatDuration(group.p99_ms)}</td>
            <td>{formatRate(group.error_rate)}</td>
            <td>{formatRate(group.timeout_rate)}</td>
            <td>{formatCount(group.by_status.rejected)}</td>
            <td>{formatCount(group.by_status.cancelled)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PlatformHealth() {
  const [windowHours, setWindowHours] = useState(WINDOW_OPTIONS[0].value);
  const [kind, setKind] = useState(KIND_OPTIONS[0].value);
  const [groupBy, setGroupBy] = useState(GROUP_OPTIONS[0].value);
  const { summary, trend, rollups, loading, failed, reload } = usePlatformHealth({
    windowHours,
    kind,
    groupBy,
  });

  return (
    <div>
      <div className={styles.header}>
        <h1>Platform Health</h1>
        <div className={styles.headerButtons}>
          <button
            onClick={reload}
            className={styles.refreshButton}
            disabled={loading}
          >
            {loading ? 'Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <div className={styles.healthControls}>
        <Picker
          label="Window"
          value={windowHours}
          options={WINDOW_OPTIONS}
          onChange={(value) => setWindowHours(Number(value))}
        />
        <Picker
          label="Measure"
          value={kind}
          options={KIND_OPTIONS}
          onChange={setKind}
        />
        <Picker
          label="Group by"
          value={groupBy}
          options={GROUP_OPTIONS}
          onChange={setGroupBy}
        />
      </div>

      {rollups?.behind && <RollupsBehind through={rollups.through} />}

      {failed && (
        <div className={styles.emptyState}>
          Couldn't load platform health. Try refreshing.
        </div>
      )}

      {summary && summary.overall.count === 0 && (
        <div className={styles.emptyState}>No events in this window.</div>
      )}

      {summary && summary.overall.count > 0 && (
        <>
          <Notes summary={summary} />
          <Tiles overall={summary.overall} kind={summary.filters.kind} />
        </>
      )}

      {trend.length > 0 && <Trend days={trend} />}

      {summary && summary.overall.count > 0 && (
        <GroupTable groups={summary.groups} groupBy={summary.group_by} />
      )}
    </div>
  );
}
